import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Col, Form, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import Select, { SingleValue } from "react-select"
import { useTranslation } from "react-i18next"
import { deleteProductCategory } from "../../../../store/product-categories/actions"
import { Category } from "../Categories"

interface DeleteCategoryProps {
  isOpen: boolean
  toggle: () => void
  category: Category | null
  allCategories: Category[]
  onDeleted?: () => void
}

type DeleteMode = "delete" | "move" | ""

export const DeleteCategory: React.FC<DeleteCategoryProps> = ({
  isOpen,
  toggle,
  category,
  allCategories,
  onDeleted,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [action, setAction] = useState<DeleteMode>("")
  const [selectedCategory, setSelectedCategory] = useState<{
    value: number
    label: string
  } | null>(null)
  const [error, setError] = useState("")

  useEffect(() => {
    if (isOpen) {
      setAction("")
      setSelectedCategory(null)
      setError("")
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!action) {
      setError(t("categories_actionValidationMessage") as string)
      return
    }

    if (action === "move" && !selectedCategory) {
      setError(t("categories_actionValidationMessage2") as string)
      return
    }

    // Dispatch delete action
    const moveToCategoryId = action === "move" ? selectedCategory?.value : undefined

    if (category) {
      dispatch(deleteProductCategory(
        category.id,
        action,
        moveToCategoryId
      ))
    }

    toggle()
    // Call onDeleted callback after successful deletion
    if (onDeleted) {
      onDeleted()
    }
  }

  const handleRadioChange = (value: DeleteMode) => {
    setAction(value)
    setError("")
    if (value !== "move") {
      setSelectedCategory(null)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <p className="text-capitalize fw-semibold font-size-24">
          {t("categories_deleteCategory")}
        </p>
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <div
            className="accordion-body py-3"
            style={{
              backgroundColor: "#FCFCFC",
              border: "1px solid #30303014",
              boxShadow: "0px 2px 20px 0px #00000040",
              borderRadius: "8px",
            }}
          >
            <h5 className="fw-semibold font-size-16 text-semi-dark mb-3">
              {t("categories_deleteCategoryMessage")}
            </h5>

            {/* Delete Option */}
            <div
              onClick={() => handleRadioChange("delete")}
              style={{
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: `2px solid ${action === "delete" ? "#007bff" : "#6c757d"}`,
                    backgroundColor: action === "delete" ? "#007bff" : "transparent",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  {action === "delete" && (
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  )}
                </div>
                <span className="fw-medium">
                  {t("categories_deleteCategoryMessage2")}
                </span>
              </div>
            </div>

            {/* Move Option */}
            <div
              onClick={() => handleRadioChange("move")}
              style={{
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <div className="d-flex align-items-start gap-3">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: `2px solid ${action === "move" ? "#007bff" : "#6c757d"}`,
                    backgroundColor: action === "move" ? "#007bff" : "transparent",
                    position: "relative",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                >
                  {action === "move" && (
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium mb-2">
                    {t("categories_moveProducts")}
                  </div>
                  {action === "move" && (
                    <Select
                      id="category"
                      value={selectedCategory}
                      onChange={(
                        selectedOption: SingleValue<{
                          value: number
                          label: string
                        }>
                      ) => {
                        setSelectedCategory(selectedOption)
                      }}
                      options={allCategories
                        .filter(cat => cat.id !== category?.id)
                        .map(cat => ({
                          value: cat.id,
                          label: cat.eN_NAME,
                        }))}
                      className="select2-selection w-100"
                    />
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="text-danger small mt-1">
                {error}
              </div>
            )}
          </div>

          <Row>
            <Col>
              <div className="d-flex justify-content-start gap-4 mt-4">
                <button
                  type="submit"
                  className="btn bg-gradiant-blue border-0 text-white"
                >
                  {t("common_save")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
